import React, { useState } from 'react';
import RichTextField from './RichTextField';
import { createWorkItem, readServerError } from '../../../api/deliveryApi';

// UDRAW-S3 §2.2 — work-item creation form. Lives INSIDE a drawer body
// (the task drawer for "+ New work item" on a costed task; the WI
// drawer for "+ New child work item" on a leaf/level<3 WI) so the
// ghost row is gone (spec §2.1). Behaviour:
//
// Fields (per spec §2.2):
//   - Title (required, max 500 chars — server also enforces).
//   - Role (only shown for top-level creates — under a parent, the
//     server inherits from the parent and ignores the field entirely,
//     per UDRAW-S3 §1.1). On a billable project the picker is
//     required; on non-billable it's hidden.
//   - Assignee (defaults to caller; team + unassigned options).
//   - Start / Finish (defaults today / today+7).
//   - Description (RichTextField → deliverable HTML).
//
// Buttons (toolbelt of the parent drawer will render these outside):
//   - Create: POST /api/projects/{id}/work-items.
//   - Cancel: onCancel() — no request.
//
// Parent-hours flow (spec §2.2 + §1.1):
//   - On 409 PARENT_HAS_HOURS the form pivots to the yellow chooser:
//     "This work item has N h logged directly. A row with hours cannot
//      get a sub-item — where should they go?"
//     [Move them to the new work item] [Delete them] [Cancel]
//   - Chosen action → repost with parentHoursAction.
//   - On 409 LOCKED_MONTH after "delete" → inline message that only
//     Move is available.
//
// Success:
//   - onCreated(dto) — the parent drawer shows the green banner +
//     "Open →" link and returns to its normal body.

const isoToday = () => new Date().toLocaleDateString('sv-SE');
const isoTodayPlus = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toLocaleDateString('sv-SE');
};

export default function CreateWorkItemForm({
  projectId,
  task,               // DeliveryTaskDto — has selectableRoles
  parentWi = null,    // DeliveryWorkItemDto when creating a child
  team = [],
  caller = null,
  isBillable = true,
  onCreated,          // (dto) => void
  onCancel,           // () => void
}) {
  const [title, setTitle] = useState('');
  const roles = Array.isArray(task?.selectableRoles) ? task.selectableRoles : [];
  const [roleId, setRoleId] = useState(roles.length === 1 ? String(roles[0].id) : '');
  const [assigneeId, setAssigneeId] = useState(
    caller?.employeeId != null ? String(caller.employeeId) : '');
  const [start, setStart] = useState(isoToday());
  const [finish, setFinish] = useState(isoTodayPlus(7));
  const [deliverable, setDeliverable] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // PARENT_HAS_HOURS chooser state — non-null when the server tells us
  // the parent has hours and asks how to handle them.
  const [hoursPrompt, setHoursPrompt] = useState(null); // { ownHours, message }
  // After a failed "delete" (LOCKED_MONTH), keep the chooser open but
  // annotate why delete is unavailable so the user can pick Move.
  const [lockedNote, setLockedNote] = useState('');

  const isChild = !!parentWi;
  const rolePickerShown = !isChild && isBillable && roles.length > 0;

  const doCreate = async (parentHoursAction = null) => {
    const t = title.trim();
    if (!t) { setError('Title is required.'); return; }
    if (rolePickerShown && !roleId) {
      setError('Pick a role — billable work items must be charged against one of the task\'s sold roles.');
      return;
    }
    setBusy(true); setError('');
    try {
      const body = {
        projectTaskId: task.id,
        parentWorkItemId: parentWi?.id ?? null,
        title: t,
        // Role is only sent for top-level creates. Under a parent the
        // server inherits from the parent (spec §1.1) so we skip it
        // rather than confuse the payload.
        projectRoleId: !isChild && roleId ? Number(roleId) : null,
        plannedStart: start || null,
        plannedFinish: finish || null,
        // MT-S3.3 — Delivery-grid create keeps the historical
        // Status="New"/no-assignee shape for owner/PM callers; team
        // members always take-over (WorkItemService.CreateAsync).
        // Assignee field here writes ONLY if the caller explicitly
        // switches away from themselves — the server does the right
        // thing based on isOwnerOrPm + SelfAssign flag.
      };
      if (parentHoursAction) body.parentHoursAction = parentHoursAction;

      const created = await createWorkItem(projectId, body);
      // Assignee is a post-create tweak — Delivery-grid flow doesn't
      // send an explicit assignee up front (team members auto-take;
      // owner/PM keeps Status=New). If a different assignee was
      // picked, this is out of scope for the create call and can be
      // adjusted through the drawer once it opens on the new WI.
      onCreated && onCreated(created);
    } catch (err) {
      // UDRAW-S3.2 HOTFIX — defensive parse of the server error body.
      // The DeliveryController's `ToError` writes `{code, message}`
      // (System.Text.Json's camelCase default for anonymous types),
      // and that's what my Jest fence mocks. On TEST though the same
      // 409 was landing without the drawer picking up `PARENT_HAS_HOURS`,
      // so the yellow chooser never opened. Two hardenings here:
      //   1. Accept the body as object OR string (some 4xx layers /
      //      buffered responses land as text; try to JSON.parse them).
      //   2. Accept `code` in either camelCase or PascalCase, and fall
      //      back to substring match on status 409 so an unexpected
      //      envelope shape still routes to the right branch instead
      //      of dropping us on a stuck "Creating…" button.
      // A single console.error dumps the raw shape so a follow-up TEST
      // trace exposes any further envelope drift without waiting for
      // another slice.
      const raw = err?.response?.data;
      const status = err?.response?.status;
      let code = null;
      let message = '';
      if (raw && typeof raw === 'object') {
        code = raw.code || raw.Code || null;
        message = (typeof raw.message === 'string' && raw.message)
          || (typeof raw.Message === 'string' && raw.Message)
          || (typeof raw.detail === 'string' && raw.detail)
          || (typeof raw.title === 'string' && raw.title)
          || '';
      } else if (typeof raw === 'string' && raw.length > 0) {
        try {
          const parsed = JSON.parse(raw);
          code = parsed.code || parsed.Code || null;
          message = parsed.message || parsed.Message || parsed.detail || '';
        } catch {
          message = raw;
          if (raw.includes('PARENT_HAS_HOURS')) code = 'PARENT_HAS_HOURS';
          else if (raw.includes('LOCKED_MONTH')) code = 'LOCKED_MONTH';
        }
      }
      // eslint-disable-next-line no-console
      console.error('[UDRAW-S3 create] error', { status, code, message, raw });

      const isParentHasHours = code === 'PARENT_HAS_HOURS'
        || (status === 409 && /parent[_ ]?has[_ ]?hours/i.test(String(message)));
      const isLockedMonth = code === 'LOCKED_MONTH'
        || (status === 409 && /locked[_ ]?month/i.test(String(message)));

      if (isParentHasHours) {
        setHoursPrompt({ message: message || 'Parent has logged hours.' });
        setLockedNote('');
      } else if (isLockedMonth && parentHoursAction === 'delete') {
        // Keep the chooser up; show the reason inline.
        setLockedNote(message ||
          'At least one hour entry falls in a locked month; move the hours instead.');
      } else {
        setError(readServerError(err) || message || 'Could not create the work item.');
        setHoursPrompt(null);
      }
    } finally {
      // Always re-enable the Create button — the previous version could
      // theoretically leave it stuck on "Creating…" if any state setter
      // above threw (never observed, but the paranoia is cheap).
      setBusy(false);
    }
  };

  if (hoursPrompt) {
    // Yellow chooser box, spec §2.2 + mockup.
    return (
      <div data-testid="wi-create-hours-chooser"
           style={{
             border: '1px solid #e6c34a', background: '#fffbea',
             borderRadius: 6, padding: 12,
           }}>
        <div style={{ fontSize: 13, marginBottom: 10 }}>{hoursPrompt.message}</div>
        <div style={{ fontSize: 12, color: '#555', marginBottom: 10 }}>
          A row with logged hours cannot become a summary — where should
          those hours go?
        </div>
        {lockedNote && (
          <div data-testid="wi-create-hours-locked-note"
               style={{
                 background: '#fdeaea', color: '#a33', border: '1px solid #e0a4a4',
                 borderRadius: 4, padding: '6px 10px', fontSize: 12, marginBottom: 10,
               }}>{lockedNote}</div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="rd-btn primary"
            data-testid="wi-create-hours-move"
            disabled={busy}
            onClick={() => doCreate('move')}
          >Move them to the new work item</button>
          <button
            type="button"
            className="rd-btn"
            data-testid="wi-create-hours-delete"
            disabled={busy || !!lockedNote}
            onClick={() => doCreate('delete')}
          >Delete them</button>
          <button
            type="button"
            className="rd-btn"
            data-testid="wi-create-hours-cancel"
            disabled={busy}
            onClick={() => { setHoursPrompt(null); setLockedNote(''); }}
          >Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wi-create-form" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div className="wi-drawer-sec-t">Title</div>
        <input
          type="text"
          className="wi-drawer-title-input"
          data-testid="wi-create-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={500}
          placeholder="Type a short, action-oriented title…"
        />
      </div>

      {rolePickerShown && (
        <div>
          <div className="wi-drawer-sec-t">Role</div>
          <select
            data-testid="wi-create-role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            <option value="">— pick a role —</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.abbreviation ? `${r.abbreviation} — ${r.roleName}` : r.roleName}
              </option>
            ))}
          </select>
        </div>
      )}

      {isChild && (
        <div>
          <div className="wi-drawer-sec-t">Role</div>
          <div className="pv" data-testid="wi-create-role-inherited">
            <span style={{ color: '#666', fontSize: 12 }}>Inherited from parent</span>
          </div>
        </div>
      )}

      <div>
        <div className="wi-drawer-sec-t">Assignee</div>
        <select
          data-testid="wi-create-assignee"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
        >
          <option value="">— unassigned —</option>
          {(team || []).map((m) => (
            <option key={m.employeeId} value={m.employeeId}>{m.fullName}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div className="wi-drawer-sec-t">Start</div>
          <input
            type="date"
            data-testid="wi-create-start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div className="wi-drawer-sec-t">Finish</div>
          <input
            type="date"
            data-testid="wi-create-finish"
            value={finish}
            onChange={(e) => setFinish(e.target.value)}
          />
        </div>
      </div>

      <div>
        <div className="wi-drawer-sec-t">Description</div>
        <RichTextField
          testId="wi-create-deliverable"
          value={deliverable}
          placeholder="What has to be delivered?"
          onSave={async (html) => { setDeliverable(html || ''); }}
        />
      </div>

      {error && (
        <div
          data-testid="wi-create-err"
          style={{
            background: '#fdeaea', color: '#a33', border: '1px solid #e0a4a4',
            borderRadius: 4, padding: '6px 10px', fontSize: 12,
          }}
        >{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="rd-btn primary"
          data-testid="wi-create-submit"
          disabled={busy}
          onClick={() => doCreate(null)}
        >{busy ? 'Creating…' : 'Create'}</button>
        <button
          type="button"
          className="rd-btn"
          data-testid="wi-create-cancel"
          disabled={busy}
          onClick={() => onCancel && onCancel()}
        >Cancel</button>
      </div>
    </div>
  );
}
